import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Menu, Loader2, HelpCircle, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CategorySidebar } from "@/components/discovery/CategorySidebar";
import { FeaturedRail } from "@/components/discovery/FeaturedRail";
import { FeedItemCard } from "@/components/discovery/FeedItemCard";
import { VendorCard } from "@/components/discovery/VendorCard";
import { EnquiryDialog } from "@/components/discovery/EnquiryDialog";

const FILTERS = [
  ["all", "All", {}],
  ["sg", "Service Guides", { content_type: "Service Guide" }],
  ["vu", "Vendor Updates", { content_type: "Vendor Update" }],
  ["vf", "Verified Feedback", { content_type: "Verified Review" }],
  ["nv", "New Vendors", { feed_tag: "new_vendor" }],
  ["of", "Offers", { feed_tag: "offer" }],
  ["ed", "Educational Content", { content_type: "Educational" }],
  ["demo", "Demo Content", { is_demo: true }],
];

function useSeo(title, desc, canonicalPath, jsonld) {
  useEffect(() => {
    if (title) document.title = title;
    const upsert = (sel, create) => { let el = document.head.querySelector(sel); if (!el) { el = create(); document.head.appendChild(el); } return el; };
    if (desc) upsert('meta[name="description"]', () => Object.assign(document.createElement("meta"), { name: "description" })).content = desc;
    upsert('meta[property="og:title"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:title"); return m; }).content = title || "";
    upsert('meta[property="og:description"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:description"); return m; }).content = desc || "";
    upsert('meta[property="og:type"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:type"); return m; }).content = "website";
    if (canonicalPath) upsert('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" })).href = window.location.origin + canonicalPath;
    let s = document.getElementById("ld-json");
    if (jsonld) { if (!s) { s = document.createElement("script"); s.id = "ld-json"; s.type = "application/ld+json"; document.head.appendChild(s); } s.textContent = JSON.stringify(jsonld); }
    return () => { document.title = "Emergent | Fullstack App"; const j = document.getElementById("ld-json"); if (j) j.remove(); };
  }, [title, desc, canonicalPath, jsonld]);
}

const Section = ({ title, children, id }) => (
  <section className="mt-8" data-testid={id}>
    <h2 className="text-lg font-display font-bold text-slate-900 mb-3">{title}</h2>
    {children}
  </section>
);

export default function VendorDiscovery() {
  const { categorySlug, subSlug } = useParams();
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState(null);
  const [sub, setSub] = useState(null);
  const [feed, setFeed] = useState([]);
  const [feedMeta, setFeedMeta] = useState({ total: 0, page: 1, has_more: false });
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState("all");
  const [drawer, setDrawer] = useState(false);

  useEffect(() => { api.get("/discovery/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setCat(null); setSub(null);
    if (subSlug && categorySlug) {
      api.get(`/discovery/category/${categorySlug}/sub/${subSlug}`).then((r) => setSub(r.data)).catch(() => {});
      api.get(`/discovery/category/${categorySlug}`).then((r) => setCat(r.data)).catch(() => {});
    } else if (categorySlug) {
      api.get(`/discovery/category/${categorySlug}`).then((r) => setCat(r.data)).catch(() => {});
    }
  }, [categorySlug, subSlug]);

  const fetchFeed = useCallback(async (page) => {
    setLoadingFeed(true);
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (categorySlug) params.set("category", categorySlug);
    if (subSlug) params.set("subcategory", subSlug);
    const fdef = FILTERS.find((f) => f[0] === filter)?.[2] || {};
    Object.entries(fdef).forEach(([k, v]) => params.set(k, String(v)));
    try {
      const r = await api.get(`/discovery/feed?${params.toString()}`);
      setFeed((prev) => (page === 1 ? r.data.items : [...prev, ...r.data.items]));
      setFeedMeta({ total: r.data.total, page: r.data.page, has_more: r.data.has_more });
    } catch { /* noop */ }
    setLoadingFeed(false);
  }, [categorySlug, subSlug, filter]);

  useEffect(() => { fetchFeed(1); }, [fetchFeed]);

  useEffect(() => {
    if (categorySlug) {
      const p = new URLSearchParams({ category: categorySlug, limit: "12" });
      if (subSlug) p.set("subcategory", subSlug);
      api.get(`/discovery/vendors?${p.toString()}`).then((r) => setVendors(r.data.items)).catch(() => {});
    } else { setVendors([]); }
  }, [categorySlug, subSlug]);

  const title = sub ? sub.name : cat ? cat.name : "Find Vendors & Service Providers";
  const intro = sub ? sub.description : cat ? cat.intro : "Browse 50+ service categories, compare providers and submit an enquiry. Neutral platform — outcomes are not guaranteed.";
  const canonicalPath = sub ? `/vendors/${cat?.slug}/${sub.slug}` : cat ? `/vendors/${cat.slug}` : "/vendors";
  const popularSubs = cat?.subcategories || [];
  const faqs = sub?.faqs || cat?.faqs || [];
  const questions = sub?.questions || cat?.questions || [];
  const jsonld = useMemo(() => {
    const crumbs = [{ "@type": "ListItem", position: 1, name: "Vendors", item: `${window.location.origin}/vendors` }];
    if (cat) crumbs.push({ "@type": "ListItem", position: 2, name: cat.name, item: `${window.location.origin}/vendors/${cat.slug}` });
    if (sub) crumbs.push({ "@type": "ListItem", position: 3, name: sub.name, item: `${window.location.origin}${canonicalPath}` });
    const graph = [{ "@type": "BreadcrumbList", itemListElement: crumbs }];
    if (faqs?.length) graph.push({ "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
    if (vendors?.length) graph.push({ "@type": "ItemList", itemListElement: vendors.map((v, i) => ({ "@type": "ListItem", position: i + 1, name: v.name })) });
    return { "@context": "https://schema.org", "@graph": graph };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, sub, vendors, canonicalPath]);
  useSeo(sub?.seo_title || cat?.seo_title || "Find Vendors | ComplaintAdvisor", sub?.seo_description || cat?.seo_description || intro, canonicalPath, jsonld);

  const Breadcrumb = (
    <nav className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap" data-testid="breadcrumb">
      <Link to="/vendors" className="hover:text-teal">Vendors</Link>
      {cat && <><ChevronRight className="h-3 w-3" /><Link to={`/vendors/${cat.slug}`} className="hover:text-teal">{cat.name}</Link></>}
      {sub && <><ChevronRight className="h-3 w-3" /><span className="text-slate-700 font-medium">{sub.name}</span></>}
    </nav>
  );

  return (
    <div className="ca-container py-6">
      <div className="lg:hidden mb-3 flex items-center justify-between gap-2">
        <Sheet open={drawer} onOpenChange={setDrawer}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" data-testid="mobile-cat-drawer-btn"><Menu className="h-4 w-4 mr-1" /> Categories</Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 overflow-y-auto p-3 bg-slate-50">
            <CategorySidebar categories={categories} activeCat={categorySlug} activeSub={subSlug} onNavigate={() => setDrawer(false)} />
          </SheetContent>
        </Sheet>
        <EnquiryDialog categorySlug={categorySlug} subcategorySlug={subSlug} categoryName={cat?.name}
          trigger={<Button size="sm" className="bg-teal-brand text-white" data-testid="mobile-enquiry-btn">Submit Enquiry</Button>} />
      </div>

      <div className="lg:grid lg:grid-cols-[250px_minmax(0,1fr)_290px] lg:gap-6 lg:items-start">
        {/* LEFT sticky */}
        <aside className="hidden lg:block lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <CategorySidebar categories={categories} activeCat={categorySlug} activeSub={subSlug} />
        </aside>

        {/* CENTER */}
        <main className="min-w-0">
          {Breadcrumb}
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-2">{title}</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">{intro}</p>
          <div className="mt-4">
            <EnquiryDialog categorySlug={categorySlug} subcategorySlug={subSlug} categoryName={cat?.name} subcategoryName={sub?.name}
              trigger={<Button className="bg-teal-brand hover:bg-teal-700 text-white" data-testid="center-enquiry-btn">Submit an Enquiry</Button>} />
          </div>

          {!categorySlug && (
            <Section title="Browse all categories" id="all-categories">
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {categories.map((c) => (
                  <Link key={c.slug} to={`/vendors/${c.slug}`} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-teal-brand/40 hover:shadow-sm transition-all" data-testid={`cat-tile-${c.slug}`}>
                    <p className="font-semibold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.intro}</p>
                    <p className="text-[11px] text-teal mt-2">{c.subcategories?.length || 0} subcategories →</p>
                  </Link>
                ))}
              </div>
            </Section>
          )}

          {questions.length > 0 && (
            <Section title="Questions to ask before hiring" id="questions">
              <ul className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                {questions.map((q, i) => <li key={i} className="text-sm text-slate-600 flex gap-2"><HelpCircle className="h-4 w-4 text-teal shrink-0 mt-0.5" /> {q}</li>)}
              </ul>
            </Section>
          )}

          {vendors.length > 0 && (
            <Section title="Relevant vendors" id="vendors-section">
              <div className="grid sm:grid-cols-2 gap-3">{vendors.map((v) => <VendorCard key={v.slug} v={v} />)}</div>
            </Section>
          )}

          <Section title="Feed" id="feed-section">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {FILTERS.map(([id, label]) => (
                <button key={id} onClick={() => setFilter(id)} data-testid={`feed-filter-${id}`}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${filter === id ? "bg-teal-brand text-white border-teal-brand" : "bg-white border-slate-200 text-slate-600 hover:border-teal-brand/40"}`}>{label}</button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mb-2">{feedMeta.total} items</p>
            <div className="space-y-3">
              {feed.map((it) => <FeedItemCard key={it.id} item={it} />)}
              {!feed.length && !loadingFeed && <p className="text-sm text-slate-400 py-6 text-center">No items match this filter.</p>}
            </div>
            <div className="mt-4 text-center">
              {loadingFeed && <Loader2 className="h-5 w-5 animate-spin text-teal mx-auto" />}
              {feedMeta.has_more && !loadingFeed && (
                <Button variant="outline" onClick={() => fetchFeed(feedMeta.page + 1)} data-testid="feed-load-more">Load more</Button>
              )}
            </div>
          </Section>

          {faqs.length > 0 && (
            <Section title="Frequently asked questions" id="faqs">
              <div className="space-y-2">
                {faqs.map((f, i) => (
                  <details key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`faq-${i}`}>
                    <summary className="text-sm font-semibold text-slate-800 cursor-pointer">{f.q}</summary>
                    <p className="text-sm text-slate-600 mt-2">{f.a}</p>
                  </details>
                ))}
              </div>
            </Section>
          )}

          {(cat?.related_categories?.length || sub?.related_subcategories?.length) > 0 && (
            <Section title={sub ? "Related subcategories" : "Related categories"} id="related">
              <div className="flex flex-wrap gap-2">
                {(sub ? sub.related_subcategories : cat.related_categories).map((r) => (
                  <Link key={r.slug} to={sub ? `/vendors/${cat.slug}/${r.slug}` : `/vendors/${r.slug}`}
                    className="rounded-full bg-slate-100 hover:bg-teal-brand/10 hover:text-teal text-slate-600 px-3 py-1.5 text-xs">{r.name}</Link>
                ))}
              </div>
            </Section>
          )}

          <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-4 flex gap-3">
            <ShieldAlert className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500">ComplaintAdvisor is a neutral platform. We do not make claims such as “best”, “guaranteed” or “government approved”. Contact details are shared only with your consent. Demo content and demo vendors are clearly labelled and are not real reviews.</p>
          </div>

          {/* Right rail stacked on mobile/tablet */}
          <div className="lg:hidden mt-8">
            <FeaturedRail categorySlug={categorySlug} categoryName={cat?.name} popularSubs={popularSubs} />
          </div>
        </main>

        {/* RIGHT sticky */}
        <aside className="hidden lg:block lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <FeaturedRail categorySlug={categorySlug} categoryName={cat?.name} popularSubs={popularSubs} />
        </aside>
      </div>
    </div>
  );
}
